import './BattleScreen.css'
import { useState, useMemo, useEffect } from 'react'
import MissileIcon from './MissileIcon'
import DropZoneMarker from './DropZoneMarker'
import HexBoard from './HexBoard'
import KillBar from './KillBar'
import Message from '../Message'
import NextRoundModal from '../NextRoundModal'
import WarResultModal from './WarResultModal'
import { STRUCTURES_TO_WIN, ROCKET_COST, DRONE_COST, ARMIES_PER_ROUND } from '../../rules/gameConfig'
import { tickYield } from '../../rules/subplotEconomy'

// Masked glyphs for the yield-tab buy buttons (runtime-resolved public URLs,
// same treatment as the board's rocket/drone glyphs).
const ROCKET_MASK = {
    WebkitMaskImage: `url(${process.env.PUBLIC_URL}/rocket_02.svg)`,
    maskImage:       `url(${process.env.PUBLIC_URL}/rocket_02.svg)`,
}
const DRONE_MASK = {
    WebkitMaskImage: `url(${process.env.PUBLIC_URL}/recon_plane_01.svg)`,
    maskImage:       `url(${process.env.PUBLIC_URL}/recon_plane_01.svg)`,
}

// ─── BattleScreen ─────────────────────────────────────────────────────────────
//
// Top-level shell for the V2/V3 battle UI.
// Composes: kill-bar header → opponent homefront → battle grid → self homefront →
//           submit row → home indicator
//
// Homefront v3: the scoreboard is a KillBar per player — one structure icon per
// structure they can lose, greyed as each is fully destroyed — and the subplot
// homefronts ARE the board: HexBoard draws both Grids on the hex lattice with
// the corridors, drones and targeting over them.
//
export default function BattleScreen({
    currentRound,
    isPaired,
    selfBoard,
    oppBoard,
    canDeploy,
    armiesToDeploy,
    isSubmitDisabled,
    pulseSubmit,
    message,
    setMessage,
    onAction,
    onSubmit,
    onReveal,
    isAnimating,
    activeClashId,
    clashResult,
    activeClashContext,
    activeClashWinner,
    activeClashSelfStrength,
    activeClashOppStrength,
    activeClashOppTargetIndex,
    activeClashSelfTargetIndex,
    activeClashAbsorbed,
    activeClashAimS,
    activeClashSelfHitInfo,
    activeClashSelfHitsLog,
    warResult,
    targetPlots,
    targetSubplots,
    onTargetPlot,
    yieldBalance,
    onHomefrontAction,
    pendingRockets,
    canAffordRocket,
    pendingDrones,
    canAffordDrone,
    onDroneTarget,
    droneFlights,
    baseBuildFx,
    selfHf,
    oppHf,
    selfLost = 0,
    oppLost = 0,
    onStructureKilled,
    structuresToWin = STRUCTURES_TO_WIN,
    warCapacities,
    dropZones,
    requiredDropZoneCount = 0,
    onDropZone,
    placingDropZones,
    resupplyDeliveries,
    revealOverlayUp,
    // True once this client has resolved the current round (the reveal is
    // computed and waiting to be played) — gates the modal's REVEAL button.
    showingPreResolution = false,
    oppReadyOverlayUp = false,
    selfXray = false,
    // Tutorial hooks: the hex the player must aim at (an arrow points at it),
    // and the eye being held or let go (onPeekChange(true | false)).
    aimArrow = null,
    // Tutorial: a white glove showing the step's gesture (HexBoard).
    gestureHint = null,
    // Tutorial: arrows at your Hive's edge and at one of your cells (HexBoard).
    hiveArrow = false,
    selfCellArrow = null,
    // Tutorial: corridors (0-based) whose deploy plus pulses.
    pulseDeploy = null,
    onPeekChange,
    // Preview only (?preview&peek): the eye stays held without a finger on it.
    peekHeld = false,
    hiveOutline = false,
    corridorSweep = false,
    columnSweep = false,
    // The war's end: shown in place of WarResultModal when given (the
    // progression prototype's own ending).
    warEnd = null,
    // The round the SUBMITTED / REVEAL banners name (NextRoundModal).
    revealRound = null,
    // Optional, in the header: the opponent's name (under Opp), a small
    // button (up and to the left of You) that opens the host's menu, and a
    // sync war's chess clocks along the bottom edge.
    oppName = null,
    menuButton = null,
    headerClock = null,
    highlightCorridors = null,
    highlightPills = true,
}) {
    const awaitingTarget = !isAnimating && selfBoard?.some(
        (bf, i) => bf.isAttacking && (targetPlots?.[i] ?? null) === null
    )
    const placedDropZones = dropZones?.length ?? 0

    // ── Yield-tab buys (rocket / drone) ──────────────────────────────────────
    // Placement mode entered from the tabs that pop out from behind the yield
    // badge; the board lights every eligible structure hex board-wide.
    const [buyPlacing, setBuyPlacing] = useState(null)   // null | 'rocket' | 'drone'

    // Build Base placement mode ({ bfId, col, uids }): entered by dropping a
    // veteran onto its corridor-mate, or from the radial BUILD BASE button.
    // uids are the two veterans funding it — the board keeps them marked as
    // the pair while the spot is picked. The corridor's self half pulses and
    // the bar previews under the pointer (red when it can't land there).
    const [basePlacing, setBasePlacing] = useState(null)

    // Intel peek — true only while the header eye button is held down.
    const [oppPeek, setOppPeek] = useState(false)

    // A tab only pops when the asset is affordable AND somewhere can take it: a
    // live structure cell with no rocket (deployed or pending) and no pending
    // drone. Rockets and drones share the same eligible-cell rule (one asset
    // per cell), so one count serves both tabs.
    const freeStructureCellCount = useMemo(() => {
        if (!selfHf?.subplots) return 0
        const droneCells = new Set((pendingDrones ?? []).map(d => d.launchS))
        let count = 0
        selfHf.subplots.forEach((cell, s) => {
            if (!cell || cell.structureId == null || cell.destroyed) return
            if ((pendingRockets ?? []).includes(s)) return
            if (selfHf.structures?.[cell.structureId]?.rocketCells?.includes(s)) return
            if (droneCells.has(s)) return
            count++
        })
        return count
    }, [selfHf, pendingRockets, pendingDrones])

    const buyGatesUp = isAnimating || !!warResult || isSubmitDisabled || revealOverlayUp
    const showRocketTab = !buyGatesUp && canAffordRocket && freeStructureCellCount > 0
    const showDroneTab  = !buyGatesUp && canAffordDrone  && freeStructureCellCount > 0

    // Drop placement modes whenever the round stops being plannable.
    useEffect(() => {
        if (buyGatesUp) {
            setBuyPlacing(null)
            setBasePlacing(null)
        }
    }, [buyGatesUp])

    const handleBuyPlace = (s) => {
        onHomefrontAction?.(s, buyPlacing === 'drone' ? 'buyDrone' : 'buyRocket')
        setBuyPlacing(null)
    }

    const awaitingDroneAim = !isAnimating && (pendingDrones ?? []).some(d => d.targetS == null)

    // Projected income of the next yield tick (flat per standing structure).
    const yieldRate = useMemo(() => (selfHf ? tickYield(selfHf).yieldIncrement : 0), [selfHf])

    // ── The live instruction ── whichever placement step is asking (base
    // placement, recon aim, choose your target, drop zones), in priority
    // order. The board lays it over the band between the halves as a
    // see-through bar (HexBoard's `prompt`), so it costs the board no height.
    const prompt = basePlacing
        ? <div className="bs__target-prompt">Slide to move your brood · release to build</div>
        : awaitingDroneAim
            ? <div className="bs__target-prompt bs__target-prompt--drone">Aim your recon plane — tap a subplot in its corridor</div>
            : awaitingTarget
                ? <div className="bs__target-prompt">Click to lock target</div>
                : placingDropZones && !isAnimating
                    ? <div className="bs__target-prompt">
                        You have {requiredDropZoneCount} egg{requiredDropZoneCount === 1 ? '' : 's'} — place your drop zone{requiredDropZoneCount === 1 ? '' : 's'} ({placedDropZones}/{requiredDropZoneCount})
                      </div>
                    : null

    return (
        <div className="bs__screen">

            <NextRoundModal
                onReveal={onReveal}
                revealOverlayUp={revealOverlayUp}
                oppReadyOverlayUp={oppReadyOverlayUp}
                roundResolved={showingPreResolution}
                revealRound={revealRound}
            />

            {warResult && (warEnd ?? <WarResultModal result={warResult} />)}

            <Message message={message} setMessage={setMessage} />

            {/* ── Kill-bar header ── */}
            <header className="bs__header">

                <div className="bs__score-you">
                    {/* One line: tag and kill icons side by side */}
                    <div className="bs__kb-row">
                        {menuButton ? (
                            // The host's menu button sits in the header's top-left
                            // corner, apart from the tag, which hugs its hexes.
                            <div className="bs__tag-menu">
                                <span className="bs__menu-slot">{menuButton}</span>
                                <span className="bs__score-tag bs__score-tag--you">You</span>
                            </div>
                        ) : <span className="bs__score-tag bs__score-tag--you">You</span>}
                        <KillBar lost={selfLost} total={structuresToWin} side="you" />
                    </div>
                </div>

                <div className="bs__score-center">
                    <div className="bs__center-top">
                        <div className={`bs__pairing-dot${isPaired ? ' bs__pairing-dot--on' : ' bs__pairing-dot--waiting'}`} />
                        <span className="bs__round-label">Rd&nbsp;{currentRound}</span>
                    </div>
                    {/* Hold-to-peek: while pressed, every corridor and airborne drone
                        lifts off the board and every still-valid bearing, on both
                        Grids, draws its structure ring (see HexBoard oppPeek). Pointer capture keeps the peek alive
                        until release even if the finger slides off the button. */}
                    <button
                        className={`bs__eye-btn${oppPeek || peekHeld ? ' bs__eye-btn--held' : ''}`}
                        aria-label="Hold to reveal structure intel on both Hives"
                        onPointerDown={(e) => {
                            e.preventDefault()
                            // Capture can throw for a pointer the browser isn't tracking
                            // (synthetic events); the peek still works without it.
                            try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch (_) { /* not capturable */ }
                            setOppPeek(true)
                            onPeekChange?.(true)
                        }}
                        onPointerUp={() => { setOppPeek(false); onPeekChange?.(false) }}
                        onPointerCancel={() => { setOppPeek(false); onPeekChange?.(false) }}
                        onPointerLeave={() => { setOppPeek(false); onPeekChange?.(false) }}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2 12c2.4-4.4 5.9-6.6 10-6.6S19.6 7.6 22 12c-2.4 4.4-5.9 6.6-10 6.6S4.4 16.4 2 12z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                    </button>
                </div>

                <div className="bs__score-opp">
                    {/* Mirrored: icons hug the tag at the outer-right edge */}
                    <div className="bs__kb-row bs__kb-row--opp">
                        <KillBar lost={oppLost} total={structuresToWin} side="opp" />
                        <span className={`bs__score-tag bs__score-tag--opp${oppName ? ' bs__score-tag--stack' : ''}`}>
                            Opp{oppName && <span className="bs__score-sub" title={oppName}>{oppName}</span>}
                        </span>
                    </div>
                </div>

                {headerClock}

            </header>

            {/* ── The board: both Grids on the hex lattice, the opponent's field
                   on top, yours on the bottom, seven corridors across. ── */}
            <HexBoard
                selfHf={selfHf}
                oppHf={oppHf}
                selfBoard={selfBoard}
                oppBoard={oppBoard}
                warCapacities={warCapacities}
                targetSubplots={targetSubplots}
                onTargetPlot={onTargetPlot}
                onAction={onAction}
                isAnimating={isAnimating}
                targetPlots={targetPlots}
                activeClashId={activeClashId}
                clashResult={clashResult}
                activeClashContext={activeClashContext}
                activeClashWinner={activeClashWinner}
                activeClashSelfStrength={activeClashSelfStrength}
                activeClashOppStrength={activeClashOppStrength}
                activeClashOppTargetIndex={activeClashOppTargetIndex}
                activeClashSelfTargetIndex={activeClashSelfTargetIndex}
                activeClashAbsorbed={activeClashAbsorbed}
                activeClashAimS={activeClashAimS}
                droneFlights={droneFlights}
                onStructureKilled={onStructureKilled}
                canDeploy={canDeploy}
                dropZones={dropZones}
                onDropZone={onDropZone}
                placingDropZones={placingDropZones}
                oppPeek={oppPeek || peekHeld}
                aimArrow={aimArrow}
                gestureHint={gestureHint}
                hiveArrow={hiveArrow}
                pulseDeploy={pulseDeploy}
                selfCellArrow={selfCellArrow}
                hiveOutline={hiveOutline}
                corridorSweep={corridorSweep}
                columnSweep={columnSweep}
                highlightCorridors={highlightCorridors}
                highlightPills={highlightPills}
                revealOverlayUp={revealOverlayUp}
                oppReadyOverlayUp={oppReadyOverlayUp}
                onHomefrontAction={onHomefrontAction}
                pendingRockets={pendingRockets}
                pendingDrones={pendingDrones}
                onDroneTarget={onDroneTarget}
                buyPlacing={buyPlacing}
                onBuyPlace={handleBuyPlace}
                onBuyCancel={() => setBuyPlacing(null)}
                basePlacing={basePlacing}
                onBasePlacingStart={(bfId, col, uids = null) => setBasePlacing({ bfId, col, uids })}
                onBasePlacingEnd={() => setBasePlacing(null)}
                resupplyDeliveries={resupplyDeliveries}
                baseBuildFx={baseBuildFx}
                prompt={prompt}
            />


            {/* ── Submit row ── */}
            <div className="bs__submit-area">


                    {/* The DOCK: the yield COIN fused to the deploy TRAY as its left end
                        cap, then the buy tabs sliding out of the tray's right edge —
                        one object reading left to right: the money, the assets it
                        buys, and (on the board) where they go. */}
                    {(() => {
                        const droneSlots = Math.max(ARMIES_PER_ROUND, armiesToDeploy ?? 0)
                        const dronesDeployed = droneSlots - (armiesToDeploy ?? 0)
                        const dzSlots = warResult ? 0 : requiredDropZoneCount
                        const allDone = (armiesToDeploy ?? 0) === 0 && placedDropZones >= dzSlots
                        return (
                            <div className={`bs__dock${isAnimating ? ' bs__dock--animating' : ''}`}>
                                {/* Yield coin: balance centred, the per-round rate tucked
                                    beneath it (keyed so a completed connection pops it). */}
                                <div className="bs__coin" data-testid="yield-coin">
                                    <span className="bs__coin-balance">${yieldBalance ?? 0}</span>
                                    <span key={yieldRate} className="bs__coin-rate bs__yield-rate">+{yieldRate}</span>
                                </div>

                                {/* Deploy tray — one drone icon per drone owed this round
                                    (ARMIES_PER_ROUND) and one drop-zone crate per drop zone
                                    owed; each goes dark grey as it is deployed / placed, left
                                    to right, so what's left to do reads as what's still lit. */}
                                <div className={`bs__tray${allDone ? ' bs__tray--done' : ''}`}>
                                    {/* Buy tabs: when a rocket/plane is affordable (and a structure
                                        cell can take it), its tab appears INSIDE the tray right after
                                        the coin — money first, then what it buys — pushing the drones
                                        right. A rounded chip in the asset's colour, the pill's own
                                        radius. Tapping one enters board-wide tap-to-place; it then
                                        reads as an X while the other tab retracts. */}
                                    {showRocketTab && (
                                        <button
                                            className={`bs__buy-tab bs__tray-tab bs__buy-tab--rocket${buyPlacing === 'rocket' ? ' bs__buy-tab--active' : ''}${buyPlacing === 'drone' ? ' bs__buy-tab--hidden' : ''}`}
                                            onClick={() => setBuyPlacing(p => p === 'rocket' ? null : 'rocket')}
                                        >
                                            {buyPlacing === 'rocket' ? (
                                                <span className="bs__buy-tab-x" />
                                            ) : (
                                                <>
                                                    <span className="bs__buy-tab-glyph" style={ROCKET_MASK} />
                                                    <span className="bs__buy-tab-cost">{ROCKET_COST}</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {showDroneTab && (
                                        <button
                                            className={`bs__buy-tab bs__tray-tab bs__buy-tab--drone${buyPlacing === 'drone' ? ' bs__buy-tab--active' : ''}${buyPlacing === 'rocket' ? ' bs__buy-tab--hidden' : ''}`}
                                            onClick={() => setBuyPlacing(p => p === 'drone' ? null : 'drone')}
                                        >
                                            {buyPlacing === 'drone' ? (
                                                <span className="bs__buy-tab-x" />
                                            ) : (
                                                <>
                                                    <span className="bs__buy-tab-glyph bs__buy-tab-glyph--drone" style={DRONE_MASK} />
                                                    <span className="bs__buy-tab-cost">{DRONE_COST}</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {Array.from({ length: droneSlots }, (_, i) => (
                                        <MissileIcon
                                            key={`d${i}`}
                                            size={22}
                                            pose="folded"
                                            color="currentColor"
                                            className={`bs__tray-icon bs__tray-drone${i < dronesDeployed ? ' bs__tray-icon--spent' : ''}`}
                                        />
                                    ))}
                                    {/* DropZoneMarker sets its colour inline (beats the class), so
                                        the spent/lit colour is passed explicitly. */}
                                    {Array.from({ length: dzSlots }, (_, i) => (
                                        <DropZoneMarker
                                            key={`z${i}`}
                                            size={18}
                                            color={i < placedDropZones ? '#4a4a4a' : '#f0e040'}
                                            className={`bs__tray-icon${i < placedDropZones ? ' bs__tray-icon--spent' : ''}`}
                                        />
                                    ))}
                                </div>

                            </div>
                        )
                    })()}

                    {/* SUBMIT. While anything is still to be placed the button is grey
                        and its sub-line says what: the drones first, then the drop
                        zones. Nothing to do → plain SUBMIT (the round number lives in
                        the header). Submitted → greyed out, plain SUBMIT (the banners
                        on the board say the round is in). */}
                    {(() => {
                        const dzRemaining = warResult ? 0 : Math.max(0, requiredDropZoneCount - placedDropZones)
                        const dronesLeft = armiesToDeploy ?? 0
                        const todo = dronesLeft >= 2 ? 'Place your drones'
                                   : dronesLeft === 1 ? 'Place 1 drone'
                                   : dzRemaining > 0 ? `Place ${dzRemaining} drop zone${dzRemaining === 1 ? '' : 's'}`
                                   : null
                        const disabled = !!(isSubmitDisabled || warResult)
                        const notready = !disabled && todo != null
                        const sub = disabled ? null : todo
                        return (
                            <button
                                className={`bs__submit-btn${disabled ? ' bs__submit-btn--disabled' : notready ? ' bs__submit-btn--notready' : ''}${pulseSubmit ? ' bs__submit-btn--pulse' : ''}`}
                                onClick={disabled ? undefined : onSubmit}
                                disabled={disabled}
                            >
                                <span className="bs__submit-main">SUBMIT</span>
                                {sub && <span className="bs__submit-sub">{sub}</span>}
                            </button>
                        )
                    })()}
            </div>

            {/* ── Home indicator ── */}
            <div className="bs__home-indicator">
                <div className="bs__home-bar" />
            </div>

        </div>
    )
}
